import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
import { Dropdown } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import './vetDatesVisited.scss';
import { isRequired } from '../../../utils/validationUtils';

export const VetDatesVisited = ({ policyNumber, dateOfBirth }) => {
  const formContext = useFormContext();
  const { resetField, clearErrors, trigger } = formContext;

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const fromMonth = useWatch({ name: `fromMonth-${policyNumber}` });
  const fromYear = useWatch({ name: `fromYear-${policyNumber}` });
  const toMonth = useWatch({ name: `toMonth-${policyNumber}` });
  const toYear = useWatch({ name: `toYear-${policyNumber}` });

  const isFromIncomplete = (fromMonth && !fromYear) || (!fromMonth && fromYear);
  const isToIncomplete = (toMonth && !toYear) || (!toMonth && toYear);
  const isToDateSelected = toMonth || toYear;

  const today = new Date();
  const currentYear = today.getFullYear();
  const birthYear = new Date(dateOfBirth).getFullYear();
  const birthMonth = new Date(dateOfBirth).getMonth();

  const monthOptions = monthNames.map((monthName, index) => {
    return {
      value: index,
      label: monthName
    };
  });

  const yearOptions = Array.from({ length: currentYear - birthYear + 1 }, (_, i) => {
    const year = currentYear - i;
    return {
      value: year,
      label: `${year}`
    };
  });

  const [fromMonthOptions, setFromMonthOptions] = useState(monthOptions);
  const [toMonthOptions, setToMonthOptions] = useState(monthOptions);

  const filterMonthsByYear = (selectedYear, selectedMonth, setMonthOptions, monthFieldName) => {
    const parsedYear = Number(selectedYear);
    const parsedMonth = Number(selectedMonth);

    if (parsedYear === birthYear) {
      const filtered = monthOptions.filter((m) => m.value >= birthMonth);
      setMonthOptions(filtered);

      if (!Number.isNaN(parsedMonth) && parsedMonth < birthMonth) {
        resetField(monthFieldName);
      }
    } else {
      setMonthOptions(monthOptions);
    }
  };

  useEffect(() => {
    if (fromYear && policyNumber) {
      filterMonthsByYear(fromYear, fromMonth, setFromMonthOptions, `fromMonth-${policyNumber}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromYear, fromMonth]);

  useEffect(() => {
    if (toYear && policyNumber) {
      filterMonthsByYear(toYear, toMonth, setToMonthOptions, `toMonth-${policyNumber}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toYear, toMonth]);

  useEffect(() => {
    const fieldsToCheck = [
      { month: fromMonth, year: fromYear, names: [`fromMonth-${policyNumber}`, `fromYear-${policyNumber}`] },
      { month: toMonth, year: toYear, names: [`toMonth-${policyNumber}`, `toYear-${policyNumber}`] }
    ];

    fieldsToCheck.forEach(({ month, year, names }) => {
      if (!month && !year) {
        clearErrors(names);
        trigger(names);
      }
    });
  }, [fromMonth, fromYear, toMonth, toYear, clearErrors, trigger, policyNumber]);

  return (
    <div className="vetVisitDatesSelect">
      <h4>Dates visited</h4>

      <div className="vetVisitDatesGroups">
        <div className="vetVisitDatesGroup fromDates">
          <h5>From</h5>
          <div className="vetDatesVisitedFields">
            <Dropdown
              key={`fromMonth-${policyNumber}-${fromYear}`}
              fieldFormContext={formContext}
              name={`fromMonth-${policyNumber}`}
              label="month"
              hideLabel
              placeholder="Select month"
              options={fromMonthOptions}
              validation={{
                required: isToDateSelected || isFromIncomplete ? isRequired(`Please select 'from' month.`) : false
              }}
              labelTooltip="Example tooltip"
            />
            <Dropdown
              fieldFormContext={formContext}
              name={`fromYear-${policyNumber}`}
              label="year"
              hideLabel
              placeholder="Select year"
              options={yearOptions}
              validation={{
                required: isToDateSelected || isFromIncomplete ? isRequired(`Please select 'from' year.`) : false
              }}
              labelTooltip="Example tooltip"
            />
          </div>
        </div>

        <div className="vetVisitDatesGroup toDates">
          <h5>To</h5>
          <div className="vetDatesVisitedFields">
            <Dropdown
              key={`toMonth-${policyNumber}-${toYear}`}
              fieldFormContext={formContext}
              name={`toMonth-${policyNumber}`}
              label="month"
              hideLabel
              placeholder="Select month"
              options={toMonthOptions}
              validation={{
                required: isToIncomplete ? isRequired("Please select 'to' month.") : false
              }}
              labelTooltip="Example tooltip"
            />
            <Dropdown
              fieldFormContext={formContext}
              name={`toYear-${policyNumber}`}
              label="year"
              hideLabel
              placeholder="Select year"
              options={yearOptions}
              validation={{
                required: isToIncomplete ? isRequired("Please select 'to' year.") : false,
                validate: () => {
                  if (!fromMonth || !fromYear || !toMonth || !toYear) return true;
                  const fromDate = new Date(fromYear, fromMonth);
                  const toDate = new Date(toYear, toMonth);

                  return toDate >= fromDate || 'To date should not be earlier than From date';
                }
              }}
              labelTooltip="Example tooltip"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

VetDatesVisited.propTypes = {
  policyNumber: PropTypes.string,
  dateOfBirth: PropTypes.string
};
